/**
 * React Query hooks for artifact download workflows.
 * Provides version listing, asset discovery, and download URL generation
 * for CodeArtifact packages scoped to a project's artifact definitions.
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import type { DownloadUrlResponse, PackageAsset, PackageVersion } from '@/api/configuration';
import {
    getArtifactDownloadUrl,
    listArtifactVersions,
    listVersionAssets,
} from '@/api/configuration';
import { queryKeys } from '@/modules/query/query-keys';

/**
 * Fetches and caches available versions for a specific artifact.
 * Enabled only when both projectKey and artifactId are provided.
 */
export function useArtifactVersions(projectKey: string, artifactId: string | null) {
  return useQuery<PackageVersion[]>({
    queryKey: queryKeys.configuration.artifactVersions(projectKey, artifactId ?? ''),
    queryFn: () => listArtifactVersions(projectKey, artifactId!),
    enabled: !!projectKey && !!artifactId,
    retry: 1,
  });
}

/**
 * Fetches and caches assets for a specific artifact version.
 * Enabled only when projectKey, artifactId, and version are all provided.
 */
export function useVersionAssets(
  projectKey: string,
  artifactId: string,
  version: string | null
) {
  return useQuery<PackageAsset[]>({
    queryKey: queryKeys.configuration.versionAssets(projectKey, artifactId, version ?? ''),
    queryFn: () => listVersionAssets(projectKey, artifactId, version!),
    enabled: !!projectKey && !!artifactId && !!version,
    retry: 1,
  });
}

/**
 * Mutation hook to generate a pre-signed download URL for an artifact asset.
 * Returns a temporary URL that can be used to initiate the browser download.
 */
export function useDownloadUrl() {
  return useMutation<
    DownloadUrlResponse,
    Error,
    { projectKey: string; artifactId: string; version: string; asset?: string }
  >({
    mutationFn: ({ projectKey, artifactId, version, asset }) =>
      getArtifactDownloadUrl(projectKey, artifactId, version, asset),
  });
}
